// Advanced search filters panel
// Date range, mood, and mode filtering

import React, { useState } from 'react';
import { Calendar, FileText, PenTool, Image, Mic } from 'lucide-react';

/**
 * SearchFilters - Advanced filtering UI
 * Provides date range, mood, and mode filters
 */
export default function SearchFilters({ filters, onUpdateFilters, onClearFilter }) {
  const [dateRangeMode, setDateRangeMode] = useState('all'); // 'all', 'week', 'month', 'year', 'custom'
  const [customStartDate, setCustomStartDate] = useState('');
  const [customEndDate, setCustomEndDate] = useState('');

  // Available moods - using colors instead of emojis
  const MOODS = [
    { value: 'happy', label: 'Happy', color: 'bg-yellow-400' },
    { value: 'sad', label: 'Sad', color: 'bg-blue-400' },
    { value: 'calm', label: 'Calm', color: 'bg-green-400' },
    { value: 'anxious', label: 'Anxious', color: 'bg-purple-400' },
    { value: 'excited', label: 'Excited', color: 'bg-pink-400' },
    { value: 'angry', label: 'Angry', color: 'bg-red-400' },
    { value: 'grateful', label: 'Grateful', color: 'bg-amber-400' },
    { value: 'neutral', label: 'Neutral', color: 'bg-gray-400' }
  ];

  // Available modes - using Lucide icons
  const MODES = [
    { value: 'general', label: 'Text', icon: FileText },
    { value: 'handwritten', label: 'Handwritten', icon: PenTool },
    { value: 'photo', label: 'Photo', icon: Image },
    { value: 'voice', label: 'Voice', icon: Mic }
  ];

  // Handle date range preset
  const handleDateRangePreset = (preset) => {
    setDateRangeMode(preset);

    if (preset === 'all') {
      onClearFilter('dateRange');
      return;
    }

    const now = new Date();
    let start = new Date();

    switch (preset) {
      case 'week':
        start.setDate(now.getDate() - 7);
        break;
      case 'month':
        start.setMonth(now.getMonth() - 1);
        break;
      case 'year':
        start.setFullYear(now.getFullYear() - 1);
        break;
      default:
        return;
    }

    onUpdateFilters({
      dateRange: { start, end: now }
    });
  };

  // Handle custom date range
  const handleCustomDateRange = () => {
    if (!customStartDate || !customEndDate) return;

    const start = new Date(customStartDate);
    const end = new Date(customEndDate);

    // Set end date to end of day
    end.setHours(23, 59, 59, 999);

    onUpdateFilters({
      dateRange: { start, end }
    });
  };

  // Toggle mood filter
  const toggleMood = (mood) => {
    const currentMoods = filters.moods || [];
    const newMoods = currentMoods.includes(mood)
      ? currentMoods.filter(m => m !== mood)
      : [...currentMoods, mood];

    if (newMoods.length === 0) {
      onClearFilter('moods');
    } else {
      onUpdateFilters({ moods: newMoods });
    }
  };

  // Toggle mode filter
  const toggleMode = (mode) => {
    const currentModes = filters.modes || [];
    const newModes = currentModes.includes(mode)
      ? currentModes.filter(m => m !== mode)
      : [...currentModes, mode];

    if (newModes.length === 0) {
      onClearFilter('modes');
    } else {
      onUpdateFilters({ modes: newModes });
    }
  };

  const selectedMoods = filters.moods || [];
  const selectedModes = filters.modes || [];

  return (
    <div className="px-5 py-3 border-t border-white/5 space-y-3">
      {/* Date Range Filter */}
      <div>
        <h4 className="text-xs text-white/25 uppercase tracking-wide mb-2">
          Time period
        </h4>
        <div className="flex flex-wrap gap-1.5">
          {[
            { value: 'all', label: 'All' },
            { value: 'week', label: 'Week' },
            { value: 'month', label: 'Month' },
            { value: 'year', label: 'Year' },
            { value: 'custom', label: 'Custom' }
          ].map(preset => (
            <button
              key={preset.value}
              onClick={() => handleDateRangePreset(preset.value)}
              className={`
                px-3 py-1.5 min-h-[36px] text-xs rounded-lg transition-all
                ${dateRangeMode === preset.value
                  ? 'bg-white/15 text-white'
                  : 'bg-white/5 text-white/35 hover:bg-white/10 hover:text-white/80'
                }
              `}
            >
              {preset.label}
            </button>
          ))}
        </div>

        {/* Custom Date Inputs */}
        {dateRangeMode === 'custom' && (
          <div className="flex gap-2 mt-2">
            <input
              type="date"
              value={customStartDate}
              onChange={(e) => setCustomStartDate(e.target.value)}
              onBlur={handleCustomDateRange}
              className="flex-1 px-2 py-1.5 bg-white/5 text-white text-xs border border-white/10 rounded-lg focus:outline-none focus:border-white/20"
            />
            <input
              type="date"
              value={customEndDate}
              onChange={(e) => setCustomEndDate(e.target.value)}
              onBlur={handleCustomDateRange}
              min={customStartDate}
              className="flex-1 px-2 py-1.5 bg-white/5 text-white text-xs border border-white/10 rounded-lg focus:outline-none focus:border-white/20"
            />
          </div>
        )}
      </div>

      {/* Mood Filter */}
      <div>
        <h4 className="text-xs text-white/25 uppercase tracking-wide mb-2">
          Mood
        </h4>
        <div className="flex flex-wrap gap-1.5">
          {MOODS.map(mood => (
            <button
              key={mood.value}
              onClick={() => toggleMood(mood.value)}
              className={`
                flex items-center gap-1.5 px-3 py-1.5 min-h-[36px] text-xs rounded-lg transition-all
                ${selectedMoods.includes(mood.value)
                  ? 'bg-white/15 text-white'
                  : 'bg-white/5 text-white/35 hover:bg-white/10 hover:text-white/80'
                }
              `}
              title={mood.label}
            >
              <span className={`w-2 h-2 rounded-full ${mood.color}`} />
              <span>{mood.label}</span>
            </button>
          ))}
        </div>
      </div>

      {/* Mode Filter */}
      <div>
        <h4 className="text-xs text-white/25 uppercase tracking-wide mb-2">
          Entry type
        </h4>
        <div className="flex flex-wrap gap-1.5">
          {MODES.map(mode => {
            const IconComponent = mode.icon;
            return (
              <button
                key={mode.value}
                onClick={() => toggleMode(mode.value)}
                className={`
                  flex items-center gap-1.5 px-3 py-1.5 min-h-[36px] text-xs rounded-lg transition-all
                  ${selectedModes.includes(mode.value)
                    ? 'bg-white/15 text-white'
                    : 'bg-white/5 text-white/35 hover:bg-white/10 hover:text-white/80'
                  }
                `}
              >
                <IconComponent size={12} />
                <span>{mode.label}</span>
              </button>
            );
          })}
        </div>
      </div>

      {/* Clear All Filters - Subtle */}
      {(filters.dateRange || selectedMoods.length > 0 || selectedModes.length > 0) && (
        <button
          onClick={() => {
            onUpdateFilters({});
            setDateRangeMode('all');
            setCustomStartDate('');
            setCustomEndDate('');
          }}
          className="w-full py-1.5 min-h-[44px] text-xs text-white/25 hover:text-white/60 transition-colors"
        >
          Clear all filters
        </button>
      )}
    </div>
  );
}
